import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import ScrollToTop from '../components/ScrollToTop';

const DonationSuccess = () => {
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');
  const token = searchParams.get('token');

  useEffect(() => {
    // Track successful donation for analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'donation_success_page_view', {
        event_category: 'donations',
        event_label: 'success_page'
      });
    }
  }, []);

  return (
    <>
      <SEO 
        title="Donation Successful - WeFlare"
        description="Thank you for your generous donation to WeFlare. Your support helps us empower African youth."
        keywords="donation successful, thank you, WeFlare, support"
      />
      <ScrollToTop />
      
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8 text-center">
            {/* Success Icon */}
            <div className="mb-4">
              <div className="success-icon-container d-inline-flex align-items-center justify-content-center rounded-circle bg-success text-white mb-4" 
                   style={{width: '120px', height: '120px', fontSize: '3rem'}}>
                <i className="bi bi-check-circle-fill"></i>
              </div>
            </div>

            {/* Success Message */}
            <h1 className="text-display mb-4" style={{color: 'var(--gray-800)'}}>
              Thank You for Your Donation!
            </h1>
            
            <p className="lead text-muted mb-4">
              Your generous contribution has been processed successfully. You're making a real difference in the lives of African youth.
            </p>

            {/* Transaction Details */}
            {(paymentId || token) && (
              <div className="card-modern p-4 mb-4 text-start">
                <h5 className="text1 mb-3">Transaction Details</h5>
                {paymentId && (
                  <div className="d-flex justify-content-between mb-2">
                    <span>Payment ID:</span>
                    <code className="text-primary">{paymentId}</code>
                  </div>
                )}
                {payerId && (
                  <div className="d-flex justify-content-between mb-2">
                    <span>Payer ID:</span>
                    <code className="text-primary">{payerId}</code>
                  </div>
                )}
                {token && (
                  <div className="d-flex justify-content-between mb-2">
                    <span>Transaction Token:</span>
                    <code className="text-primary">{token}</code>
                  </div>
                )}
                <div className="d-flex justify-content-between">
                  <span>Date:</span>
                  <span>{new Date().toLocaleDateString()}</span>
                </div>
              </div>
            )}

            {/* What Happens Next */}
            <div className="card-modern p-4 mb-4 text-start">
              <h5 className="text1 mb-3">What Happens Next?</h5>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="d-flex align-items-start">
                    <div className="flex-shrink-0">
                      <i className="bi bi-envelope-check fs-4 text-primary"></i>
                    </div>
                    <div className="ms-3">
                      <h6>Email Receipt</h6>
                      <small className="text-muted">You'll receive a confirmation email with your donation receipt within a few minutes.</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-start">
                    <div className="flex-shrink-0">
                      <i className="bi bi-graph-up-arrow fs-4 text-success"></i>
                    </div>
                    <div className="ms-3">
                      <h6>Impact Updates</h6>
                      <small className="text-muted">We'll send you regular updates on how your donation is making a difference.</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-start">
                    <div className="flex-shrink-0">
                      <i className="bi bi-people fs-4 text-warning"></i>
                    </div>
                    <div className="ms-3">
                      <h6>Join Community</h6>
                      <small className="text-muted">Connect with other supporters and stay engaged with our mission.</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Impact Statement */}
            <div className="bg-primary-light p-4 rounded-3 mb-4">
              <h5 className="text-primary mb-3">Your Impact</h5>
              <p className="text-muted mb-0">
                Your donation directly supports our programs in education, health, youth empowerment, and community development. 
                Together, we're building a brighter future for African youth and creating lasting positive change in communities across the continent.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="d-flex flex-wrap gap-3 justify-content-center mb-4">
              <Link to="/" className="btn-modern btn-primary">
                <i className="bi bi-house me-2"></i>
                Return Home
              </Link>
              <Link to="/programs/overview" className="btn-modern btn-outline">
                <i className="bi bi-eye me-2"></i>
                See Our Programs
              </Link>
              <Link to="/about-us/team" className="btn-modern btn-outline">
                <i className="bi bi-people me-2"></i>
                Meet Our Team
              </Link>
            </div>

            {/* Social Sharing */}
            <div className="text-center">
              <p className="text-muted mb-3">Help us spread the word:</p>
              <div className="d-flex gap-2 justify-content-center">
                <a 
                  href={`https://twitter.com/intent/tweet?text=I just donated to @WeFlare to support African youth empowerment! Join me in making a difference. ${window.location.origin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="bi bi-twitter me-1"></i>
                  Share on Twitter
                </a>
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.origin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="bi bi-facebook me-1"></i>
                  Share on Facebook
                </a>
                <a 
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.origin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="bi bi-linkedin me-1"></i>
                  Share on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DonationSuccess;